import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import EToast from '@/components/everridge/EToast';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useTranslation } from 'react-i18next';
import {
    CreateResponseDto,
    CreateTenantRequestDto,
    TenantDetailsResponseDto,
    UpdateTenantRequestDto,
} from '@/features/superAdmin/features/tenants/types/superAdmin-tenants-dto';

type Props = {} & ApiHookProps;

export const useTenantDetailsApi = ({ id }: Props) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const {
        data: tenantDetailsData,
        isLoading: isLoadingTenantDetailsData,
        isError: isErrorTenantDetailsData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.tenants.queryKeys.details,
            id
        ],
        queryFn: async (): Promise<TenantDetailsResponseDto> => {
            const url = ApiConfig.queries.superAdmin.tenants.endpoints.get.replace("{id}", id || '');
            const response = await axiosInstance.get<TenantDetailsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
    });

    const {
        mutateAsync: createTenant,
        isPending: isCreatingTenant,
        isError: isErrorCreatingTenant
    } = useMutation({
        mutationFn: async (dto: CreateTenantRequestDto) => {
            const response = await axiosInstance.post<CreateResponseDto>(
                ApiConfig.queries.superAdmin.tenants.endpoints.create, dto
            );
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.list]
            });
            EToast({
                variant: 'success',
                message: t('main.superAdmin.tenants.toasts.create.success.message'),
                description: t('main.superAdmin.tenants.toasts.create.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.tenants.toasts.create.error.message'),
                description: t('main.superAdmin.tenants.toasts.create.error.description')
            });
        }
    });

    const {
        mutateAsync: updateTenant,
        isPending: isUpdatingTenant,
        isError: isErrorUpdatingTenant
    } = useMutation({
        mutationFn: async (dto: UpdateTenantRequestDto) => {
            const url = ApiConfig.queries.superAdmin.tenants.endpoints.update.replace("{id}", id || '');
            const response = await axiosInstance.put<UpdateTenantRequestDto>(url, dto);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.list]
            });
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.details, id]
            });
            EToast({
                variant: 'success',
                message: t('main.superAdmin.tenants.toasts.update.success.message'),
                description: t('main.superAdmin.tenants.toasts.update.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.tenants.toasts.update.error.message'),
                description: t('main.superAdmin.tenants.toasts.update.error.description')
            });
        }
    });

    const {
        mutateAsync: uploadTenantLogo,
        isPending: isUploadingTenantLogo,
    } = useMutation({
        mutationFn: async ({ tenantId, file }: { tenantId: string; file: File }) => {
            const formData = new FormData();
            formData.append('file', file);

            const url = ApiConfig.queries.superAdmin.tenants.endpoints.uploadLogo.replace('{id}', tenantId);
            await axiosInstance.put(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        },
        onSuccess: async (_, { tenantId }) => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.details, tenantId]
            });
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.auth.queryKeys.tenants]
            });
        },
    });

    const {
        mutateAsync: deleteTenant,
        isPending: isDeletingTenant,
        isError: isErrorDeletingTenant
    } = useMutation({
        mutationFn: async () => {
            const url = ApiConfig.queries.superAdmin.tenants.endpoints.delete.replace("{id}", id || '');
            await axiosInstance.delete(url);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.list]
            });
            queryClient.removeQueries({
                queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.details, id]
            });
            EToast({
                variant: 'success',
                message: t('main.superAdmin.tenants.toasts.delete.success.message'),
                description: t('main.superAdmin.tenants.toasts.delete.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.tenants.toasts.delete.error.message'),
                description: t('main.superAdmin.tenants.toasts.delete.error.description')
            });
        }
    });

    return {
        tenantDetailsData,
        isLoadingTenantDetailsData,
        isErrorTenantDetailsData,
        createTenant,
        isCreatingTenant,
        isErrorCreatingTenant,
        updateTenant,
        isUpdatingTenant,
        isErrorUpdatingTenant,
        uploadTenantLogo,
        isUploadingTenantLogo,
        deleteTenant,
        isDeletingTenant,
        isErrorDeletingTenant,
    }
};
